import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchAdminDashboard } from '../../store/slices/adminSlice';
import { Card } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { statusBadge } from '../../components/ui/Button';
import { formatUSD, formatDate } from '../../utils/formatters';
import { Ico } from '../../components/ui/Icon';

const AdminDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { stats } = useAppSelector(s => s.admin);

  useEffect(() => {
    dispatch(fetchAdminDashboard());
  }, [dispatch]);

  const statCards = [
    { label: 'Total Users',      value: stats?.total_users || 0,          icon: <Ico name="user" />,        color: 'text-blue-400',   bg: 'bg-blue-500/10' },
    { label: 'Active Users',     value: stats?.active_users || 0,         icon: <Ico name="user" />,        color: 'text-primary',    bg: 'bg-primary/10' },
    { label: 'Open Trades',      value: stats?.open_trades || 0,          icon: <Ico name="chart" />,   color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
    { label: 'Real Funds',       value: formatUSD(stats?.total_real_funds || 0), icon: <Ico name="wallet" />, color: 'text-primary', bg: 'bg-primary/10' },
    { label: 'Pending Deposits', value: stats?.pending_deposits || 0,     icon: <Ico name="arrow_down" />,   color: 'text-orange-400', bg: 'bg-orange-500/10' },
    { label: 'Pending Withdrawals', value: stats?.pending_withdrawals || 0, icon: <Ico name="arrow_up" />, color: 'text-red-500',   bg: 'bg-red-500/10' },
    { label: 'Total Deposited',  value: formatUSD(stats?.deposit_volume || 0), icon: <Ico name="arrow_down" />, color: 'text-primary', bg: 'bg-primary/10' },
    { label: 'Total Withdrawn',  value: formatUSD(stats?.withdrawal_volume || 0), icon: <Ico name="arrow_up" />, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  ];

  const quickLinks = [
    { to: '/admin/deposits', label: 'Review Deposits', count: stats?.pending_deposits, color: 'text-primary border-primary/20 bg-primary/5' },
    { to: '/admin/withdrawals', label: 'Review Withdrawals', count: stats?.pending_withdrawals, color: 'text-orange-400 border-orange-500/20 bg-orange-500/5' },
    { to: '/admin/kyc', label: 'Review KYC', color: 'text-blue-400 border-blue-500/20 bg-blue-500/5' },
    { to: '/admin/users', label: 'Manage Users', color: 'text-yellow-400 border-yellow-500/20 bg-yellow-500/5' },
  ];

  return (
    <div className="p-4 md:p-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {statCards.map(card => (
          <Card key={card.label} className="flex flex-col gap-3">
            <div className={`w-9 h-9 rounded-xl ${card.bg} flex items-center justify-center ${card.color}`}>
              {card.icon}
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wider">{card.label}</p>
              <p className={`font-display font-bold text-xl mt-0.5 ${card.color}`}>{card.value}</p>
            </div>
          </Card>
        ))}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {quickLinks.map(link => (
          <Link
            key={link.to}
            to={link.to}
            className={`flex items-center justify-between p-4 rounded-xl border transition-all hover:scale-[1.02] ${link.color}`}
          >
            <div>
              <p className="font-display font-semibold text-sm">{link.label}</p>
              {link.count !== undefined && link.count > 0 && (
                <p className="text-xs mt-0.5 opacity-70">{link.count} pending</p>
              )}
            </div>
            <Ico name="arrow_right" />
          </Link>
        ))}
      </div>
    </div>
  );
};

export default AdminDashboard;
